import { useEffect, useRef } from 'react';

/**
 * Las subpestañas de la casa (Cartas, Mazos y Meta), en un solo sitio.
 *
 * Estaban escritas TRES veces con el mismo marcado copiado, y las tres tenian
 * la misma deuda: eran `role=tablist` de mentira. Un lector de pantalla las
 * anunciaba como fichas -- "pestaña 2 de 4" -- y quien las oye asi espera lo
 * que hacen las fichas en cualquier otro programa: moverse con las flechas.
 * Aqui las flechas no hacian nada y habia que tabular pestaña por pestaña,
 * saliendo por el otro lado si te pasabas. Prometer un patron y no cumplirlo
 * es peor que no prometerlo.
 *
 * Lo que hace este componente es cumplirlo:
 *
 *   - TABULADOR ITINERANTE. Solo la pestaña activa entra en el recorrido del
 *     tabulador (`tabIndex 0`; el resto, -1). Asi la fila entera cuenta como
 *     UNA parada -- que es lo que es -- y Tab lleva al contenido en vez de
 *     obligar a recorrer cuatro botones antes de llegar a la tabla.
 *   - FLECHAS ← → para moverse, Home y End para los extremos, y en circulo:
 *     desde la ultima, la flecha derecha vuelve a la primera.
 *
 * ACTIVACION AUTOMATICA (la flecha ya cambia de pestaña, no hace falta Enter):
 * es lo que hace el gesto de siempre y lo que se espera. Tiene un coste --
 * recorrer las cuatro del meta con la flecha pide los datos de las cuatro --
 * y se asume a proposito: quien va pasando pestañas quiere verlas, y las
 * peticiones ya van cacheadas y con guarda de vista viva.
 */
export default function Subtabs({ items, value, onChange, label, className = '' }) {
  const ref = useRef(null);
  /*
   * PESTAÑA NUEVA, PÁGINA POR ARRIBA.
   *
   * Cambiar de subpestaña heredaba el desplazamiento de la anterior: con el
   * meta desplazado hasta el arquetipo #9, pulsar "Torneos" dejaba a media
   * lista de torneos, en un sitio que nadie eligió. En el teléfono es peor,
   * porque la barra vive abajo y el gesto de cambiar de sección se hace
   * justamente estando lejos del principio.
   *
   * Es la misma decisión que ya toma App.jsx al cambiar de VISTA, y va aquí --
   * en el componente compartido -- porque si no habría que acordarse en Cartas,
   * en Mazos y en el Meta por separado, y el que se olvide no da la cara.
   *
   * Sólo al PULSAR: montar la vista con una pestaña que viene de la URL, o
   * volver con "atrás", no toca el scroll (ahí manda lo que el navegador
   * restaure), y con las flechas tampoco (ver `alTeclado`). Sin animación a
   * propósito: es un salto de contexto, y un desplazamiento suave de dos
   * pantallas se siente lento, no elegante.
   */
  const elegir = (key) => {
    if (key !== value) window.scrollTo({ top: 0 });
    onChange(key);
  };
  /*
   * El foco ACOMPAÑA a la seleccion, pero solo cuando el cambio vino del
   * teclado. Sin esta bandera, cambiar de pestaña con el raton robaria el foco
   * y pintaria el anillo dorado sobre un boton que nadie ha enfocado -- el
   * anillo es una señal de "estas aqui con el teclado", y encenderlo tras un
   * clic lo convierte en ruido.
   */
  const porTeclado = useRef(false);
  useEffect(() => {
    if (!porTeclado.current) return;
    porTeclado.current = false;
    ref.current?.querySelector('.subtabs__btn.is-active')?.focus();
  }, [value]);

  const alTeclado = (e) => {
    const paso = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
    const extremo = { Home: 0, End: items.length - 1 }[e.key];
    let destino = null;
    if (paso != null) {
      const i = items.findIndex((it) => it.key === value);
      destino = items[(i + paso + items.length) % items.length];
    } else if (extremo != null) {
      destino = items[extremo];
    }
    if (!destino) return;
    // Sin esto la flecha ademas desplaza la pagina por debajo.
    e.preventDefault();
    /*
     * Con las flechas NO se sube al principio, y es a proposito: el foco
     * acompaña a la seleccion (ver abajo), asi que llevar la pagina arriba
     * arrastraria el anillo dorado fuera de donde esta mirando quien pulsa.
     * Quien navega con teclado esta EN la fila de pestañas y la ve; quien
     * pulsa con el dedo, no.
     */
    porTeclado.current = true;
    onChange(destino.key);
  };

  return (
    <nav
      className={`subtabs ${className}`.trim()}
      role="tablist"
      aria-label={label}
      onKeyDown={alTeclado}
      ref={ref}
    >
      {items.map((p) => {
        const activa = value === p.key;
        return (
          <button
            key={p.key}
            type="button"
            role="tab"
            aria-selected={activa}
            tabIndex={activa ? 0 : -1}
            className={`subtabs__btn${activa ? ' is-active' : ''}`}
            onClick={() => elegir(p.key)}
          >
            {p.short ? (
              /* Rotulo corto para el telefono, como el `short` del dock: en la
                 fila a mitades exactas de movil, un rotulo de dos palabras
                 ("Matriz completa") se partia en dos lineas y estiraba la fila
                 entera. Decide el CSS, no JS: son el mismo boton. */
              <>
                <span className="subtabs__rotulo">{p.label}</span>
                <span className="subtabs__rotulo--corto">{p.short}</span>
              </>
            ) : (
              p.label
            )}
            {/* Cuántos hay dentro, cuando el número ayuda a elegir («Mis mazos
                5»): es el olor de información de la pestaña — se sabe qué
                espera detrás antes de pulsar. Insignia de dato, sin borde. */}
            {p.n != null && <span className="subtabs__n">{p.n}</span>}
          </button>
        );
      })}
    </nav>
  );
}
